<template>
  <div class="vehicle-data tw-flex tw-flex-col tw-items-center tw-mt-8">
    <br />
    <br />
    <!-- Title and Navigation -->
    <div class="tw-w-full">
      <!-- Form Section -->
      <div class="form-section tw-mt-6">
        <h3 style="color: #114cac" class="tw-text-2xl tw-font-semibold tw-mb-4">
          Quote Details
        </h3>
        <!-- Divider Line -->
        <hr class="tw-border-gray-300 tw-my-4" />

        <!-- Form Fields for Vehicle Details -->
        <v-form ref="form" v-model="isFormValid">
          <div
            class="form-grid tw-grid tw-gap-4 tw-grid-cols-1 md:tw-grid-cols-3"
          >
            <!-- Vehicle Registration Number Field -->
            <v-text-field
              v-model="form.vehicleRegNumber"
              label="Vehicle Registration Number"
              class="custom-input"
              :rules="[
                required('Vehicle Registration Number'),
                numberPlateFormat(),
              ]"
              placeholder="eg: KBL 123W."
              @input="toUppercase"
            />

            <v-select
              v-model="form.vehicleMakeId"
              :items="makeList"
              label="Vehicle Make"
              item-text="makeName"
              item-value="makeId"
              class="custom-input"
              placeholder="Select Vehicle Make"
              @change="onMakeChange"
            />
            <v-select
              v-model="form.vehicleModel"
              :items="modelList"
              label="Vehicle Model"
              class="custom-input"
              :rules="[required('Vehicle Model')]"
              item-text="modelName"
              item-value="modelName"
              placeholder="Select Vehicle Model"
              @change="onModelChange"
            />

            <v-select
              v-model="form.bodyType"
              :items="bodyTypes"
              label="Vehicle Body Type"
              class="custom-input"
              :rules="[required('Vehicle Body Type')]"
              placeholder="Select Body Type"
            />

            <v-text-field
              v-model="form.yearOfManufacture"
              label="Year of Manufacture"
              type="number"
              class="custom-input"
              :rules="[required('Year of Manufacture'), minYear()]"
              placeholder="YYYY"
            ></v-text-field>

            <v-text-field
              v-model="form.estimatedValue"
              label="Estimated Vehicle Value"
              type="text"
              class="custom-input"
              :rules="[required('Estimated Vehicle Value')]"
              placeholder="Estimated Vehicle Value (KSh)"
              @input="formatNumber('estimatedValue')"
            />

            <v-text-field
              v-model="form.entertainmentValue"
              label="Estimated value of Entertainment"
              type="text"
              class="custom-input"
              placeholder="Estimated value of Entertainment (KSh)"
              @input="formatNumber('entertainmentValue')"
            ></v-text-field>
            <v-text-field
              v-model="form.windscreenValue"
              label="Estimated value of Windscreen"
              type="text"
              class="custom-input"
              placeholder="Estimated value of Windscreen (KSh)"
              @input="formatNumber('windscreenValue')"
            ></v-text-field>

            <v-text-field
              v-model="form.engineNumber"
              label="Engine Number"
              class="custom-input"
              :rules="[required('Engine Number')]"
              placeholder="Enter Engine No."
              @input="toUppercase"
            ></v-text-field>

            <v-text-field
              v-model="form.engineCapacity"
              label="Engine Capacity (CC)"
              type="number"
              class="custom-input"
              :rules="[required('Engine Capacity'), maxEngineCapacity()]"
              placeholder="Enter Engine Capacity"
            />

            <v-text-field
              v-model="form.seatingCapacity"
              label="Seating Capacity"
              type="number"
              class="custom-input"
              :rules="[required('Seating Capacity'), seatingCapacityRange()]"
              placeholder="Enter seating capacity"
            />

            <v-text-field
              v-model="form.logbookNumber"
              label="Logbook Number"
              class="custom-input"
              placeholder="Enter Logbook No."
              @input="toUppercase"
            ></v-text-field>

            <v-text-field
              v-model="form.chassisNumber"
              label="Vehicle Chassis Number"
              class="custom-input"
              :rules="[
                required('Vehicle Chassis Number'),
                minLength('Vehicle Chassis Number', 6),
              ]"
              placeholder="Enter Chassis No."
              @input="toUppercase"
            ></v-text-field>

            <v-text-field
              v-model="form.color"
              label="Vehicle Color"
              class="custom-input"
              :rules="[required('Vehicle Color')]"
              placeholder="Enter Vehicle Color"
              @input="toUppercase"
            ></v-text-field>
          </div>
          <v-card-actions
            class="tw-flex tw-justify-center col-sm-4 offset-sm-4 tw-mt-5 tw-pb-8"
          >
            <v-btn
              block
              large
              color="primary"
              class="my-3"
              @click="submitForm"
              :disabled="!isFormValid"
            >
              {{ isFormValid ? "Next" : "Check if all fields are filled" }}
            </v-btn>
          </v-card-actions>
        </v-form>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { mapGetters, mapState } from 'vuex';
import validations from '@/utils/validations';
import helperMixins from '@/mixins/helperMixins';
import { log } from '@/utils/errorLogger';
import secureValidation from '@/utils/secureValidation';

export default {
  name: 'VehicleData',
  mixins: [helperMixins],
  data() {
    return {
      isFormValid: false,
      form: {
        vehicleRegNumber: '',
        vehicleMake: null,
        vehicleModel: '',
        bodyType: '',
        yearOfManufacture: '',
        estimatedValue: '',
        engineNumber: '',
        engineCapacity: '',
        seatingCapacity: '',
        logbookNumber: '',
        chassisNumber: '',
        color: '',
        entertainmentValue: '',
        windscreenValue: '',
        quoteId: null,
        quoteExluded: null,
      },
      bodyTypes: [],
      makeList: [],
      modelList: [],
      selectedMakeId: null,
    };
  },

  computed: {
    ...mapState({
      user: (state) => state.auth.user,
    }),
    ...mapGetters('auth', ['isAuthenticated']),
  },

  async mounted() {
    await this.fetchVehicleMakes();
    await this.fetchVehicleTypeList();

    this.loadVehicleDetailsFromStorage();
  },

  methods: {
    formatCurrencyValue(value) {
      return value ? secureValidation.formatNumberSecure(value.toString()) : '';
    },
    // Inside methods:
    loadVehicleDetailsFromStorage() {
      const savedVehicleDetails = JSON.parse(localStorage.getItem('vehicleDetails'));
      const storedQuoteId = localStorage.getItem('quoteId');

      if (!savedVehicleDetails) return;

      const vehicle = savedVehicleDetails.vehicle;
      this.form.vehicleRegNumber = vehicle.regNo || '';
      this.form.vehicleMakeId = vehicle.makeId || null;

      if (this.form.vehicleMakeId) {
        if (this.makeList.length > 0) {
          const makeObj = this.makeList.find((m) => m.makeId === this.form.vehicleMakeId);
          if (makeObj) this.form.vehicleMake = makeObj.makeName;
        }
        this.fetchVehicleModels(this.form.vehicleMakeId);
      }

      this.form.vehicleModel = vehicle.model || '';
      this.form.bodyType = vehicle.bodyType || '';
      this.form.yearOfManufacture = vehicle.yearOfManufacture || '';
      this.form.estimatedValue = this.formatCurrencyValue(vehicle.value);
      this.form.entertainmentValue = this.formatCurrencyValue(vehicle.entertainmentValue);
      this.form.windscreenValue = this.formatCurrencyValue(vehicle.windscreenValue);
      this.form.engineNumber = vehicle.engineNo || '';
      this.form.engineCapacity = vehicle.cc || '';
      this.form.seatingCapacity = vehicle.seatingCapacity || '';
      this.form.logbookNumber = vehicle.logbookNumber || '';
      this.form.chassisNumber = vehicle.chassisNumber || '';
      this.form.color = vehicle.vehicleColor || '';
      this.form.quoteId = storedQuoteId || null;
    },
    ...validations,
    formatNumber(field) {
      const val = this.form[field];
      if (!val) return;

      const el = document.activeElement;
      // Calculate how many digits are before the cursor
      const digitsBefore = val.toString().slice(0, el.selectionStart).replaceAll(/\D/g, '').length;

      // Format the number
      const formatted = secureValidation.formatNumberSecure(val.toString().replaceAll(/\D/g, ''));
      this.form[field] = formatted;

      // Restore cursor position based on digit count
      this.$nextTick(() => {
        let pos = 0;
        let d = 0;
        // Advance position until we've passed the same number of digits
        while (pos < formatted.length && d < digitsBefore) {
          if (/\d/.test(formatted[pos++])) d++; // eslint-disable-line no-plusplus
        }
        el.setSelectionRange(pos, pos);
      });
    },

    getRawNumber(field) {
      return this.form[field]
        ? parseInt(this.form[field].replace(/,/g, ''), 10)
        : null;
    },
    maxEngineCapacity() {
      return (v) => {
        const isValid = v && v.toString().length <= 5;
        return (
          isValid
          || 'Engine Capacity must not exceed 5 digits (e.g., max: 20000)'
        );
      };
    },
    seatingCapacityRange() {
      return (v) => {
        const isValid = v && v >= 1 && v <= 8;
        return isValid || 'Seating Capacity must be between 1 and 8';
      };
    },
    onMakeChange(selectedMakeId) {
      const selectedMake = this.makeList.find(
        (item) => item.makeId === selectedMakeId,
      );
      this.form.vehicleMake = selectedMake ? selectedMake.makeName : '';
      this.$nextTick(() => {
        this.validateFormManually();
      });
      this.fetchVehicleModels(selectedMakeId);
    },
    onModelChange(selectedModel) {
      const selected = this.modelList.find(
        (item) => item.modelName === selectedModel,
      );
      this.form.quoteExcluded = selected ? selected.quoteExcluded : null;
    },

    async fetchVehicleMakes() {
      try {
        const response = await axios.post('/motor/api/quote/motorMakeList');
        if (response.data.code === 0) {
          this.makeList = response.data.data.map((item) => ({
            makeName: item.makeName,
            makeId: item.makeId,
          }));
        } else {
          this.$toast.error('Failed to fetch vehicle makes');
        }
      } catch (error) {
        log(error, this, 'fetchVehicleMakes');
        this.$toast.error('Failed to fetch vehicle makes', error);
      }
    },

    async fetchVehicleTypeList() {
      try {
        const response = await axios.post('/motor/api/quote/vehicleTypeList');
        if (response.data && response.data.data) {
          this.bodyTypes = response.data.data;
        }
      } catch (error) {
        log(error, this, 'fetchVehicleTypeList');
        this.$toast.info('Please log in to view your quotation.');
      }
    },

    async fetchVehicleModels(makeId) {
      if (!makeId) {
        this.modelList = [];
        return;
      }

      try {
        const response = await axios.post('/motor/api/quote/motorModelList', {
          makeId,
        });
        if (response.data.code === 0 && response.data.data) {
          this.modelList = response.data.data.map((item) => ({
            modelName: item.modelName,
            quoteExcluded: item.quoteExcluded, // Extract `quoteExcluded`
          }));
        } else {
          this.modelList = [];
        }
      } catch (error) {
        log(error, this, 'fetchVehicleModels');
        this.$toast.error('Something went wrong. Please try again.');
        this.modelList = [];
      }
    },

    toUppercase() {
      this.form.vehicleRegNumber = this.form.vehicleRegNumber.toUpperCase();
      this.form.color = this.form.color.toUpperCase();
      this.form.logbookNumber = this.form.logbookNumber.toUpperCase();
      this.form.chassisNumber = this.form.chassisNumber.toUpperCase();
      this.form.engineNumber = this.form.engineNumber.toUpperCase();
    },

    minYear() {
      const currentYear = new Date().getFullYear();
      return (v) => (v >= 1980 && v <= currentYear)
        || `Year must be between 1980 and ${currentYear}`;
    },

    async submitForm(event) {
      if (!this.isFormValid) return;
      event.preventDefault();

      const formPayload = JSON.parse(localStorage.getItem('formPayload')) || {};

      const payload = {
        agentId: '',
        customerId: formPayload.customerId,
        product: 'MOTOR-P',
        productGroup: 'private',
        quoteId: this.form.quoteId,
        appUser: 'P',
        vehicle: {
          bodyType: this.form.bodyType,
          cc: this.form.engineCapacity,
          chassisNumber: this.form.chassisNumber,
          engineNo: this.form.engineNumber,
          entertainmentValue: this.getRawNumber('entertainmentValue') || 0,
          logbookNumber: this.form.logbookNumber || 0,
          make: this.form.vehicleMake,
          makeId: this.form.vehicleMakeId,
          model: this.form.vehicleModel,
          quoteExcluded: this.form.quoteExcluded,
          regNo: this.form.vehicleRegNumber,
          seatingCapacity: this.form.seatingCapacity,
          value: this.getRawNumber('estimatedValue'),
          vehicleColor: this.form.color,
          windscreenValue: this.getRawNumber('windscreenValue') || 0,
          yearOfManufacture: this.form.yearOfManufacture,
        },
      };
      localStorage.setItem('vehicleDetails', JSON.stringify(payload));
      try {
        const response = await axios.post('/motor/api/quote/create', payload);
        this.$toast.success(response.data.message);
        if (response.data.data && response.data.data.quoteId) {
          localStorage.setItem('quoteId', response.data.data.quoteId);
        }
        this.$emit('next');
      } catch (error) {
        log(error, this, 'submitForm');
        this.$toast.error('Something went wrong');
      }
    },
  },
};
</script>

<style scoped>
.vehicle-data {
  top: 10%;
}

.form-grid {
  gap: 1rem;
}
.custom-input .v-label {
  color: #114cac;
  font-size: 1.1rem;
}
.custom-input input::placeholder {
  color: #a0a0a0 !important;
}
.custom-input .v-input__control {
  border-bottom: 1px solid #e0e0e0 !important;
}
.vehicle-data >>> .v-label {
  color: #114cac;
  font-size: 1.1rem;
}
</style>
